import React, { useState, useEffect } from 'react';
import {
  TrendingUp, TrendingDown, Users, CheckCircle, XCircle,
  Clock, Zap, Heart, Target, Award, BarChart3
} from 'lucide-react';
import { behavioralEngine } from '../utils/behavioralAnalytics';
import { autonomousEngine } from '../utils/autonomousActions';

const KPIDashboard = () => {
  const [kpis, setKpis] = useState({
    // مؤشرات رضا العملاء
    customerSatisfaction: {
      current: 94.5,
      previous: 89.2,
      target: 95,
      trend: 'up'
    },
    // تقليل التذاكر
    ticketReduction: {
      current: 42,
      previous: 28,
      target: 50,
      trend: 'up',
      unit: '%'
    },
    // معدل الحل التلقائي
    autoResolutionRate: {
      current: 67.8,
      previous: 52.3,
      target: 75,
      trend: 'up',
      unit: '%'
    },
    // وقت الاستجابة المتوسط
    avgResponseTime: {
      current: 1.2,
      previous: 3.5,
      target: 1.0,
      trend: 'down',
      unit: 'دقيقة'
    },
    // دقة التنبؤ
    predictionAccuracy: {
      current: 85.3,
      previous: 78.9,
      target: 90,
      trend: 'up',
      unit: '%'
    },
    // معدل التدخل الاستباقي
    proactiveInterventionRate: {
      current: 73.5,
      previous: 45.2,
      target: 80,
      trend: 'up',
      unit: '%'
    }
  });

  const [analytics, setAnalytics] = useState(null);
  const [actionStats, setActionStats] = useState(null);

  useEffect(() => {
    // تحديث البيانات من المحركات
    const updateData = () => {
      const analyticsData = behavioralEngine.getAnalyticsSummary();
      const actionsData = autonomousEngine.getActionStats();

      setAnalytics(analyticsData);
      setActionStats(actionsData);

      // تحديث KPIs بناءً على البيانات الفعلية
      if (analyticsData && actionsData) {
        setKpis(prev => ({
          ...prev,
          customerSatisfaction: {
            ...prev.customerSatisfaction,
            current: parseFloat(analyticsData.satisfactionRate) || prev.customerSatisfaction.current
          },
          autoResolutionRate: {
            ...prev.autoResolutionRate,
            current: parseFloat(actionsData.successRate) || prev.autoResolutionRate.current
          }
        }));
      }
    };

    updateData();
    const interval = setInterval(updateData, 5000);

    return () => clearInterval(interval);
  }, []);

  const KPICard = ({ title, icon: Icon, data, color }) => {
    const isPositiveTrend = data.trend === 'up' ? 
      (data.current > data.previous) : 
      (data.current < data.previous);

    const percentageChange = data.previous !== 0 
      ? (((data.current - data.previous) / data.previous) * 100).toFixed(1)
      : 0;

    const progressPercentage = (data.current / data.target) * 100;

    return (
      <div className={`bg-white rounded-xl shadow-lg p-6 border-l-4 border-${color}-500`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full bg-${color}-100 flex items-center justify-center`}>
              <Icon className={`w-6 h-6 text-${color}-600`} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-600">{title}</h3>
              <p className="text-3xl font-bold text-gray-800">
                {data.current}{data.unit || ''}
              </p>
            </div>
          </div>
          <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${
            isPositiveTrend ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {isPositiveTrend ? (
              <TrendingUp className="w-4 h-4" />
            ) : (
              <TrendingDown className="w-4 h-4" />
            )}
            <span className="text-sm font-bold">{Math.abs(percentageChange)}%</span>
          </div>
        </div>

        {/* شريط التقدم */}
        <div className="mb-2">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>التقدم نحو الهدف</span>
            <span>{data.target}{data.unit || ''}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`bg-${color}-500 h-2 rounded-full transition-all duration-500`}
              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
            ></div>
          </div>
        </div>

        <div className="flex justify-between text-xs text-gray-500 mt-2">
          <span>السابق: {data.previous}{data.unit || ''}</span>
          <span className={progressPercentage >= 100 ? 'text-green-600 font-bold' : ''}>
            {progressPercentage >= 100 ? '✓ تم تحقيق الهدف' : `${progressPercentage.toFixed(0)}% من الهدف`}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold mb-2 flex items-center gap-3">
              <BarChart3 className="w-8 h-8" />
              مؤشرات الأداء الرئيسية (KPIs)
            </h2>
            <p className="text-purple-100">
              قياس دقة التنبؤ، رضا العملاء، وفعالية النظام الاستباقي
            </p>
          </div>
          <div className="text-center">
            <Award className="w-16 h-16 mx-auto mb-2 opacity-80" />
            <p className="text-sm font-medium">الأداء الإجمالي</p>
            <p className="text-3xl font-bold">A+</p>
          </div>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <KPICard
          title="رضا العملاء"
          icon={Heart}
          data={kpis.customerSatisfaction}
          color="pink"
        />
        <KPICard
          title="تقليل التذاكر"
          icon={TrendingDown}
          data={kpis.ticketReduction}
          color="green"
        />
        <KPICard
          title="معدل الحل التلقائي"
          icon={Zap}
          data={kpis.autoResolutionRate}
          color="yellow"
        />
        <KPICard
          title="وقت الاستجابة"
          icon={Clock}
          data={kpis.avgResponseTime}
          color="blue"
        />
        <KPICard
          title="دقة التنبؤ"
          icon={Target}
          data={kpis.predictionAccuracy}
          color="purple"
        />
        <KPICard
          title="التدخل الاستباقي"
          icon={CheckCircle}
          data={kpis.proactiveInterventionRate}
          color="indigo"
        />
      </div>

      {/* Analytics Summary */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-indigo-600" />
              ملخص المستخدمين
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">إجمالي المستخدمين</span>
                <span className="text-2xl font-bold text-indigo-600">{analytics.totalUsers}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">إجمالي التفاعلات</span>
                <span className="text-2xl font-bold text-blue-600">{analytics.totalInteractions}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <span className="text-gray-600">مستخدمين في حالة حرجة</span>
                <span className="text-2xl font-bold text-red-600">{analytics.criticalUsers}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                <span className="text-gray-600">مستخدمين عالي الخطورة</span>
                <span className="text-2xl font-bold text-orange-600">{analytics.highRiskUsers}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-gray-600">مستخدمين راضين</span>
                <span className="text-2xl font-bold text-green-600">{analytics.satisfiedUsers}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Zap className="w-6 h-6 text-yellow-600" />
              الإجراءات التلقائية
            </h3>
            {actionStats && (
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">إجمالي الإجراءات</span>
                  <span className="text-2xl font-bold text-purple-600">{actionStats.total}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-gray-600">إجراءات ناجحة</span>
                  <span className="text-2xl font-bold text-green-600">{actionStats.successful}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="text-gray-600">إجراءات فاشلة</span>
                  <span className="text-2xl font-bold text-red-600">{actionStats.failed}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-gray-600">معدل النجاح</span>
                  <span className="text-2xl font-bold text-blue-600">{actionStats.successRate}%</span>
                </div>

                {/* أنواع الإجراءات */}
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium text-gray-600 mb-2">توزيع الإجراءات:</p>
                  {Object.entries(actionStats.byType).map(([type, count]) => (
                    <div key={type} className="flex justify-between items-center text-sm py-1">
                      <span className="text-gray-600">{type}</span>
                      <span className="font-bold text-gray-800">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Impact Summary */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg p-6 text-white">
        <h3 className="text-2xl font-bold mb-4">📊 التأثير الإجمالي للنظام الاستباقي</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-4xl font-bold mb-1">42%</p>
            <p className="text-sm opacity-90">تقليل في التذاكر الواردة</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold mb-1">67.8%</p>
            <p className="text-sm opacity-90">حل تلقائي بدون تدخل بشري</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold mb-1">94.5%</p>
            <p className="text-sm opacity-90">رضا العملاء</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold mb-1">1.2 د</p>
            <p className="text-sm opacity-90">متوسط وقت الاستجابة</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KPIDashboard;
